import { Seo } from "@/components/Seo";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  invokeInternshipNotification,
  submitInternshipApplication,
} from "@/lib/supabase-marketing";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import internAvatar1 from "@/assets/intern-avatar-1.png";
import internAvatar2 from "@/assets/intern-avatar-2.png";
import internAvatar3 from "@/assets/intern-avatar-3.png";
import noBusyworkIcon from "@/assets/no-busywork-icon.svg";
import directAccessIcon from "@/assets/direct-access-icon.svg";
import ideasGoLiveIcon from "@/assets/ideas-go-live-icon.svg";
import internshipCollabImg from "@/assets/internship-collab.jpg";
import ctaCollabPhoto from "@/assets/cta-collab-photo.jpg";
import InternshipTestimonials from "@/components/InternshipTestimonials";
import { Check, Mail, Phone, MapPin } from "lucide-react";

const applicationSchema = z.object({
  full_name: z.string().trim().min(1, "Full name is required").max(100).regex(/^[a-zA-ZÀ-ÖØ-öø-ÿ\s'-]+$/, "Name can only contain letters, spaces, hyphens, and apostrophes"),
  university_name: z.string().trim().min(1, "University name is required").max(200),
  email: z.string().trim().email("Please enter a valid email").max(255),
  instagram_handle: z.string().trim().max(100).optional().or(z.literal("")),
  tiktok_handle: z.string().trim().max(100).optional().or(z.literal("")),
  agrees_to_communications: z.literal(true, { errorMap: () => ({ message: "You must agree to continue" }) }),
});

type ApplicationFormValues = z.infer<typeof applicationSchema>;

const PerchInternship = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const form = useForm<ApplicationFormValues>({
    resolver: zodResolver(applicationSchema),
    defaultValues: {
      full_name: "",
      university_name: "",
      email: "",
      instagram_handle: "",
      tiktok_handle: "",
      agrees_to_communications: false as unknown as true,
    },
  });

  const onSubmit = async (values: ApplicationFormValues) => {
    setIsSubmitting(true);
    try {
      await submitInternshipApplication({
        full_name: values.full_name,
        university_name: values.university_name,
        email: values.email,
        instagram_handle: values.instagram_handle || null,
        tiktok_handle: values.tiktok_handle || null,
        agrees_to_communications: values.agrees_to_communications,
      });
      setSubmitted(true);
      toast.success("Application submitted successfully!");

      // Fire-and-forget internal notification — don't block the user
      invokeInternshipNotification({
        full_name: values.full_name,
        email: values.email,
        university_name: values.university_name,
        instagram_handle: values.instagram_handle || null,
        tiktok_handle: values.tiktok_handle || null,
        agrees_to_communications: values.agrees_to_communications,
      }).catch((err) => console.error("Notification email failed:", err));
    } catch {
      toast.error("We're having trouble submitting your application. Please try again. If the issue continues, contact us and we'll help right away.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    const formEl = document.getElementById("internship-form");
    formEl?.scrollIntoView({ behavior: "smooth" });
    setTimeout(() => {
      const nameInput = formEl?.querySelector<HTMLInputElement>("input[name='full_name']");
      nameInput?.focus();
    }, 600);
  };

  return (
    <div className="min-h-screen">
      <Seo
        title="Perch Internship Program"
        description="Join the Perch Mobile internship program. Gain real-world experience in mobile technology, fintech, and credit building."
        path="/perch-internship"
      />
      <Navigation />

      {/* Band 1: Hero + Application Form */}
      <section
        className="pt-[74px] flex items-center relative overflow-hidden"
        style={{
          minHeight: "max(800px, 100vh)",
          background: "#242424",
        }}
      >
        {/* Top-left ellipse blur */}
        <div
          className="absolute rounded-full pointer-events-none"
          style={{
            width: "310px",
            height: "310px",
            top: "-40px",
            left: "-62px",
            background: "#B0F18D80",
            filter: "blur(430px)",
          }}
        />
        {/* Bottom-right purple ellipse blur */}
        <div
          className="absolute rounded-full pointer-events-none"
          style={{
            width: "405px",
            height: "405px",
            top: "731px",
            right: "-100px",
            background: "#533DDA",
            filter: "blur(430px)",
          }}
        />
        <div className="max-w-[1514px] w-full mx-auto px-4 md:px-8 lg:px-[76px] py-8 lg:py-0 relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-12 lg:gap-8">
            {/* Left column — copy */}
            <div className="flex flex-col gap-6 max-w-[600px]">
              {/* Badge */}
              <span
                className="inline-flex items-center justify-center self-start rounded-full"
                style={{
                  width: "153px",
                  height: "41px",
                  background: "#533DDA",
                  color: "#FFFFFF",
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 500,
                  fontSize: "16px",
                  lineHeight: "40px",
                  letterSpacing: "-0.05em",
                }}
              >
                Perch Internship
              </span>

              {/* Headline */}
              <h1
                className="font-semibold"
                style={{
                  fontFamily: "Instrument Sans, sans-serif",
                  fontSize: "75px",
                  lineHeight: "74px",
                  letterSpacing: "-0.05em",
                  fontWeight: 600,
                  color: "#FFFFFF",
                }}
              >
                Build The Future{" "}
                <span style={{ color: "rgba(255,255,255,0.4)" }}>of</span>
                <br />
                <span style={{ color: "#B0F18D" }}>Mobile</span>
              </h1>

              {/* Subtext */}
              <p
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 600,
                  fontSize: "25px",
                  lineHeight: "44px",
                  letterSpacing: "-0.05em",
                  color: "#FFFFFF",
                }}
              >
                We're looking for students who see
                <br className="hidden sm:block" /> differently and build boldly.
              </p>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={scrollToForm}
                  className="rounded-[11px] px-6 font-semibold text-base transition-opacity hover:opacity-90 whitespace-nowrap"
                  style={{
                    height: "59px",
                    border: "1px solid #FFFFFFB2",
                    background: "#B0F18D",
                    color: "#000000",
                    fontFamily: "Instrument Sans, sans-serif",
                  }}
                >
                  Apply For The Paid Marketing Internship
                </button>
                <button
                  onClick={() => document.getElementById("this-isnt-busywork")?.scrollIntoView({ behavior: "smooth" })}
                  className="rounded-[11px] px-6 font-semibold text-base border transition-colors hover:bg-white/5 whitespace-nowrap"
                  style={{
                    height: "59px",
                    borderColor: "#FFFFFFB2",
                    borderWidth: "1px",
                    color: "#FFFFFF",
                    fontFamily: "Instrument Sans, sans-serif",
                  }}
                >
                  Learn More About The Role
                </button>
              </div>

              {/* Trust badge */}
              <div className="flex items-center gap-4 mt-10">
                <div className="flex -space-x-3">
                  {[internAvatar1, internAvatar2, internAvatar3].map((src, i) => (
                    <div
                      key={i}
                      className="rounded-full overflow-hidden flex-shrink-0"
                      style={{
                        width: "62.59px",
                        height: "62.59px",
                        border: "2.5px solid #FFFFFFB2",
                        boxShadow: "0px 4px 84px 0px #00000040",
                      }}
                    >
                      <img src={src} alt="" className="w-full h-full object-cover" />
                    </div>
                  ))}
                  <div
                    className="rounded-full flex items-center justify-center font-bold text-white flex-shrink-0"
                    style={{
                      width: "62.59px",
                      height: "62.59px",
                      border: "2.5px solid #FFFFFFB2",
                      background: "linear-gradient(167.93deg, #4833C8 8.81%, #231962 80.28%)",
                      boxShadow: "0px 4px 84px 0px #00000040",
                      fontSize: "30px",
                    }}
                  >
                    +
                  </div>
                </div>
                <p
                  className="whitespace-nowrap"
                  style={{
                    fontFamily: "Instrument Sans, sans-serif",
                    fontWeight: 600,
                    fontSize: "23px",
                    lineHeight: "62.59px",
                    letterSpacing: "-0.05em",
                    background: "linear-gradient(90.11deg, #FFFFFF 12.78%, #999999 95.05%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Trusted by <span style={{ fontWeight: 700 }}>100+</span> students
                </p>
              </div>
            </div>

            {/* Right column — form card */}
            <div
              id="internship-form"
              className="w-full lg:max-w-[511px] rounded-2xl p-6 md:p-8 relative overflow-hidden"
              style={{
                background: "linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)",
                minHeight: "732px",
              }}
            >
              {/* Gradient border overlay */}
              <div
                className="absolute inset-0 rounded-2xl pointer-events-none"
                style={{
                  padding: "1px",
                  background: "linear-gradient(98.15deg, #FFFFFF 62.94%, #A4A4A4 99.21%)",
                  WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                  WebkitMaskComposite: "xor",
                  maskComposite: "exclude",
                }}
              />
              {submitted ? (
                <div className="flex flex-col items-center justify-center py-12 text-center gap-4">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: "#B0F18D" }}>
                    <span className="text-2xl">✓</span>
                  </div>
                  <h3 className="text-xl font-semibold text-white" style={{ fontFamily: "Instrument Sans, sans-serif" }}>
                    Application Received!
                  </h3>
                  <p className="text-white/70 text-sm">We'll be in touch soon.</p>
                </div>
              ) : (
                <>
                  <h2
                    className="mb-6"
                    style={{
                      fontFamily: "Instrument Sans, sans-serif",
                      fontSize: "30px",
                      fontWeight: 600,
                      lineHeight: "36px",
                      letterSpacing: "-0.05em",
                      color: "rgba(255,255,255,0.85)",
                    }}
                  >
                    If you're ready to build{" "}
                    <span style={{ color: "rgba(255,255,255,0.4)" }}>what's</span>
                    <br />
                    built different,{" "}
                    <span style={{ color: "#B0F18D" }}>apply now.</span>
                  </h2>

                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
                      <FormField
                        control={form.control}
                        name="full_name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-white" style={{ fontFamily: "Inter, sans-serif", fontSize: "18px", fontWeight: 600, lineHeight: "40px", letterSpacing: "-0.05em" }}>Full name</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Enter your full name"
                                className="bg-transparent border-white/20 text-white placeholder:text-white/50 placeholder:font-semibold rounded-[10px] [font-family:Inter,sans-serif] text-sm tracking-[-0.05em]"
                                style={{ height: "52px" }}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="university_name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-white" style={{ fontFamily: "Inter, sans-serif", fontSize: "18px", fontWeight: 600, lineHeight: "40px", letterSpacing: "-0.05em" }}>University name</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Enter your university name"
                                className="bg-transparent border-white/20 text-white placeholder:text-white/50 placeholder:font-semibold rounded-[10px] [font-family:Inter,sans-serif] text-sm tracking-[-0.05em]"
                                style={{ height: "52px" }}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-white" style={{ fontFamily: "Inter, sans-serif", fontSize: "18px", fontWeight: 600, lineHeight: "40px", letterSpacing: "-0.05em" }}>Email address</FormLabel>
                            <FormControl>
                              <Input
                                type="text"
                                placeholder="Enter your email address"
                                className="bg-transparent border-white/20 text-white placeholder:text-white/50 placeholder:font-semibold rounded-[10px] [font-family:Inter,sans-serif] text-sm tracking-[-0.05em]"
                                style={{ height: "52px" }}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* Social handles row */}
                      <div className="grid grid-cols-2 gap-3">
                        <FormField
                          control={form.control}
                          name="instagram_handle"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-white font-semibold text-sm" style={{ fontFamily: "Inter, sans-serif", fontSize: "18px", fontWeight: 600, lineHeight: "40px", letterSpacing: "-0.05em" }}>Instagram</FormLabel>
                              <FormControl>
                                  <Input
                                    placeholder="Enter Instagram handle"
                                    className="bg-transparent text-white placeholder:text-white/50 placeholder:font-semibold rounded-[10px] [font-family:Inter,sans-serif] text-sm tracking-[-0.05em]"
                                    style={{ height: "52px", border: "1px solid #F2F2F2" }}
                                    {...field}
                                  />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="tiktok_handle"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-white font-semibold text-sm" style={{ fontFamily: "Inter, sans-serif", fontSize: "18px", fontWeight: 600, lineHeight: "40px", letterSpacing: "-0.05em" }}>Tiktok</FormLabel>
                              <FormControl>
                                  <Input
                                    placeholder="Enter TikTok handle"
                                    className="bg-transparent text-white placeholder:text-white/50 placeholder:font-semibold rounded-[10px] [font-family:Inter,sans-serif] text-sm tracking-[-0.05em]"
                                    style={{ height: "52px", border: "1px solid #F2F2F2" }}
                                    {...field}
                                  />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Checkbox */}
                      {/* Consent section */}
                      <div
                        className="rounded-[1px] p-4"
                        style={{
                          border: "0.5px solid #4F4F4F",
                          background: "transparent",
                        }}
                      >
                        <FormField
                          control={form.control}
                          name="agrees_to_communications"
                          render={({ field }) => (
                            <FormItem className="flex flex-col gap-0 space-y-0 mb-2">
                              <div className="flex flex-row items-center gap-2">
                                <FormControl>
                                  <Checkbox
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                    className="border-white data-[state=checked]:bg-white data-[state=checked]:text-black rounded-[1px]"
                                    style={{ width: "14px", height: "14px" }}
                                  />
                                </FormControl>
                                <div className="leading-none">
                                  <FormLabel
                                    className="text-white cursor-pointer"
                                    style={{ fontFamily: "Inter, sans-serif", fontSize: "14px", fontWeight: 600, lineHeight: "40px", letterSpacing: "-0.05em" }}
                                  >
                                    I agree to receive other communications from Perch.
                                  </FormLabel>
                                </div>
                              </div>
                              <FormMessage className="text-red-400 text-xs mt-1" />
                            </FormItem>
                          )}
                        />

                        <p
                          className="text-white"
                          style={{ fontFamily: "Inter, sans-serif", fontSize: "9px", fontWeight: 400, lineHeight: "17px", letterSpacing: "-0.05em" }}
                        >
                          By submitting this form, you consent to Perch Mobile processing your data and contacting you to
                          fulfil your request. For more information on how we are committed to protecting and respecting
                          your privacy, please review our{" "}
                          <Link to="/privacy-policy" className="underline">
                            Privacy Policy
                          </Link>
                          .
                        </p>
                      </div>

                      {/* Submit */}
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full rounded-[11px] font-semibold text-base transition-opacity hover:opacity-90 disabled:opacity-50"
                        style={{
                          background: "#000000",
                          color: "#FFFFFF",
                          fontFamily: "Instrument Sans, sans-serif",
                          height: "59px",
                        }}
                      >
                        {isSubmitting ? "Submitting…" : "Submit Application"}
                      </button>
                    </form>
                  </Form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Band 2: "This Isn't Busywork" */}
      <section id="this-isnt-busywork" style={{ background: "#27243A" }}>
        <div className="max-w-[1514px] mx-auto px-4 md:px-8 lg:px-[76px] py-20 flex flex-col items-center" style={{ minHeight: "741px" }}>
          <h2
            className="text-center capitalize"
            style={{
              fontFamily: "Instrument Sans, sans-serif",
              fontWeight: 600,
              fontSize: "55px",
              lineHeight: "74px",
              letterSpacing: "-0.05em",
              color: "#FFFFFF",
              maxWidth: "1229px",
            }}
          >
            This Isn't Busywork. <span style={{ color: "#B0F18D" }}>It's Building.</span>
          </h2>

          <p
            className="text-center mt-4"
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 600,
              fontSize: "25px",
              lineHeight: "44px",
              letterSpacing: "-0.05em",
              color: "#FFFFFFB8",
              maxWidth: "824px",
            }}
          >
            You won't be shadowing from the sidelines. You'll launch ideas, shape campaigns, and see your work go live from day one.
          </p>

          {/* Three feature cards */}
          <div
            className="flex flex-col md:flex-row gap-6 mt-12 w-full justify-center"
            style={{ maxWidth: "1287px" }}
          >
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="relative overflow-hidden flex-1 w-full mx-auto"
                style={{
                  height: "259px",
                  maxWidth: "391px",
                  minWidth: "280px",
                  borderRadius: "40px",
                  background: "linear-gradient(103.65deg, rgba(82,82,82,0.7) 45.29%, rgba(205,205,205,0.273) 103.03%), linear-gradient(0deg, rgba(0,0,0,0.13), rgba(0,0,0,0.13))",
                  backdropFilter: "blur(20px)",
                  boxShadow: "0px 14px 84px 30px #00000026",
                }}
              >
                {/* Gradient border */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    borderRadius: "40px",
                    padding: "0.5px",
                    background: "linear-gradient(98.15deg, #FFFFFF 62.94%, #A4A4A4 99.21%)",
                    WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                    WebkitMaskComposite: "xor",
                    maskComposite: "exclude",
                  }}
                />
                {/* Noise texture overlay */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    borderRadius: "40px",
                    opacity: 0.2,
                    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
                    backgroundSize: "128px 128px",
                  }}
                />
                {/* Card content */}
                {i === 0 && (
                  <div className="relative z-10 p-[31px_40px]">
                    <img
                      src={noBusyworkIcon}
                      alt="No Busywork"
                      style={{ width: "37.65px", height: "36px" }}
                    />
                    <p
                      className="mt-[18px] font-inter font-semibold text-white"
                      style={{
                        fontSize: "40px",
                        lineHeight: "40px",
                        letterSpacing: "-0.05em",
                      }}
                    >
                      No{" "}<br />
                      <span className="text-secondary">Busywork</span>
                    </p>
                    <p
                      className="font-inter font-semibold"
                      style={{
                        fontSize: "16px",
                        lineHeight: "30px",
                        letterSpacing: "-0.05em",
                        color: "#FFFFFFE5",
                        marginTop: "10px",
                      }}
                    >
                      → Only real impact
                    </p>
                  </div>
                )}
                {i === 1 && (
                  <div className="relative z-10 p-[31px_40px]">
                    <img
                      src={directAccessIcon}
                      alt="Direct Access"
                      style={{ width: "37.65px", height: "36px" }}
                    />
                    <p
                      className="mt-[18px] font-inter font-semibold text-white"
                      style={{
                        fontSize: "40px",
                        lineHeight: "40px",
                        letterSpacing: "-0.05em",
                      }}
                    >
                      Direct{" "}<br />
                      <span className="text-secondary">Access</span>
                    </p>
                    <p
                      className="font-inter font-semibold"
                      style={{
                        fontSize: "16px",
                        lineHeight: "30px",
                        letterSpacing: "-0.05em",
                        color: "#FFFFFFE5",
                        marginTop: "10px",
                      }}
                    >
                      → Work side-by-side with founders.
                    </p>
                  </div>
                )}
                {i === 2 && (
                  <div className="relative z-10 p-[31px_40px]">
                    <img
                      src={ideasGoLiveIcon}
                      alt="Ideas Go Live"
                      style={{ width: "37.65px", height: "36px" }}
                    />
                    <p
                      className="mt-[18px] font-inter font-semibold text-white"
                      style={{
                        fontSize: "40px",
                        lineHeight: "40px",
                        letterSpacing: "-0.05em",
                      }}
                    >
                      Ideas{" "}<br />
                      <span className="text-secondary">Go Live</span>
                    </p>
                    <p
                      className="font-inter font-semibold"
                      style={{
                        fontSize: "16px",
                        lineHeight: "30px",
                        letterSpacing: "-0.05em",
                        color: "#FFFFFFE5",
                        marginTop: "10px",
                      }}
                    >
                      → What you build actually launches.
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Band 3: "You / Us" */}
      <section
        style={{ background: "#27243A" }}
      >
        <div
          className="max-w-[1514px] mx-auto px-4 md:px-8 lg:px-[76px] flex flex-col items-center"
          style={{ minHeight: "1180px", paddingTop: "109px", paddingBottom: "80px" }}
        >
          {/* Hero image */}
          <div
            className="relative w-full"
            style={{
              maxWidth: "1300px",
              borderRadius: "40px",
              marginBottom: "120px",
            }}
          >
            <div className="w-full overflow-hidden" style={{ height: "867px", borderRadius: "40px" }}>
              <img
                src={internshipCollabImg}
                alt="Interns collaborating"
                className="w-full h-full object-cover"
              />
            </div>

            {/* YOU / US cards container */}
            <div
              className="absolute left-1/2 -translate-x-1/2 flex gap-[61px]"
              style={{
                width: "1191px",
                maxWidth: "calc(100% - 48px)",
                height: "410px",
                bottom: "-99px",
              }}
            >
              {/* YOU card */}
              <div
                className="relative overflow-hidden flex-1"
                style={{
                  maxWidth: "565px",
                  height: "396px",
                  borderRadius: "40px",
                  background: "linear-gradient(103.65deg, rgba(82,82,82,0.7) 45.29%, rgba(205,205,205,0.273) 103.03%), linear-gradient(0deg, rgba(0,0,0,0.13), rgba(0,0,0,0.13))",
                  backdropFilter: "blur(20px)",
                  alignSelf: "flex-end",
                }}
              >
                {/* Gradient border */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    borderRadius: "40px",
                    padding: "0.5px",
                    background: "linear-gradient(98.15deg, #FFFFFF 62.94%, #A4A4A4 99.21%)",
                    WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                    WebkitMaskComposite: "xor",
                    maskComposite: "exclude",
                  }}
                />
                <div className="relative z-10 p-[40px] flex flex-col justify-center h-full">
                  <h3
                    style={{
                      fontFamily: "Instrument Sans, sans-serif",
                      fontWeight: 600,
                      fontSize: "65px",
                      lineHeight: "74px",
                      letterSpacing: "-0.05em",
                      color: "#B0F18D",
                    }}
                  >
                    YOU
                  </h3>
                  <ul className="mt-4 flex flex-col gap-0">
                    {[
                      "You understand social media.",
                      "You see what makes content shareable.",
                      "You don't wait to be told what to do.",
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-center gap-3" style={{ height: "43px" }}>
                        <span style={{ color: "#B0F18D", fontSize: "18px", lineHeight: "43px" }}>✓</span>
                        <span
                          style={{
                            fontFamily: "Inter, sans-serif",
                            fontWeight: 600,
                            fontSize: "16px",
                            lineHeight: "43px",
                            color: "#FFFFFF",
                          }}
                        >
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* US card */}
              <div
                className="relative overflow-hidden flex-1"
                style={{
                  maxWidth: "565px",
                  height: "396px",
                  borderRadius: "40px",
                  background: "linear-gradient(103.65deg, rgba(82,82,82,0.7) 45.29%, rgba(205,205,205,0.273) 103.03%), linear-gradient(0deg, rgba(0,0,0,0.13), rgba(0,0,0,0.13))",
                  backdropFilter: "blur(20px)",
                  alignSelf: "flex-end",
                }}
              >
                {/* Gradient border */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    borderRadius: "40px",
                    padding: "0.5px",
                    background: "linear-gradient(98.15deg, #FFFFFF 62.94%, #A4A4A4 99.21%)",
                    WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                    WebkitMaskComposite: "xor",
                    maskComposite: "exclude",
                  }}
                />
                <div className="relative z-10 p-[40px] flex flex-col justify-center h-full">
                  <h3
                    style={{
                      fontFamily: "Instrument Sans, sans-serif",
                      fontWeight: 600,
                      fontSize: "65px",
                      lineHeight: "74px",
                      letterSpacing: "-0.05em",
                      color: "#B0F18D",
                    }}
                  >
                    US
                  </h3>
                  <ul className="mt-4 flex flex-col gap-0">
                    {[
                      "We give real ownership.",
                      "Your ideas don't sit in decks. They go live.",
                      "We test. Launch. Improve.",
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-center gap-3" style={{ height: "43px" }}>
                        <span style={{ color: "#B0F18D", fontSize: "18px", lineHeight: "43px" }}>✓</span>
                        <span
                          style={{
                            fontFamily: "Inter, sans-serif",
                            fontWeight: 600,
                            fontSize: "16px",
                            lineHeight: "43px",
                            color: "#FFFFFF",
                          }}
                        >
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <InternshipTestimonials />

      {/* Band 5: CTA "Ready to Take Your Perch?" */}
      <section style={{ background: "#533DDA", position: "relative", overflow: "hidden" }}>
        {/* Blur ellipse 1 */}
        <div
          style={{
            position: "absolute",
            width: "609px",
            height: "609px",
            top: "-76px",
            left: "-191px",
            borderRadius: "50%",
            background: "radial-gradient(circle, #FFFFFF0F 0%, transparent 70%)",
            filter: "blur(80px)",
            pointerEvents: "none",
          }}
        />
        {/* Blur ellipse 2 */}
        <div
          style={{
            position: "absolute",
            width: "474px",
            height: "474px",
            top: "375px",
            left: "803px",
            borderRadius: "50%",
            background: "radial-gradient(circle, #EDFF910D 0%, transparent 70%)",
            filter: "blur(80px)",
            pointerEvents: "none",
          }}
        />
        <div className="max-w-[1512px] w-full mx-auto px-4 md:px-8 lg:px-[76px] relative z-10" style={{ minHeight: "696px", display: "flex", alignItems: "center" }}>
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-12 lg:gap-16">
            {/* Left content */}
            <div className="flex-1">
              {/* Badge */}
              <span
                className="inline-flex items-center justify-center mb-8"
                style={{
                  width: "171px",
                  height: "38px",
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 600,
                  fontSize: "16px",
                  letterSpacing: "-0.05em",
                  color: "#FFFFFF",
                  background: "#000000",
                  borderRadius: "10px",
                }}
              >
                Perch Internship
              </span>

              <h2
                style={{
                  fontFamily: "'Instrument Sans', sans-serif",
                  fontWeight: 600,
                  fontSize: "56px",
                  lineHeight: "57px",
                  letterSpacing: "-0.05em",
                  whiteSpace: "nowrap",
                  background: "linear-gradient(90.11deg, #FFFFFF 12.78%, #999999 95.05%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  marginBottom: "24px",
                }}
              >
                Ready to Take Your Perch?
              </h2>

              {/* Subtext */}
              <p
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 600,
                  fontSize: "25px",
                  lineHeight: "39px",
                  letterSpacing: "-0.05em",
                  textTransform: "capitalize" as const,
                  color: "#FFFFFFCC",
                  maxWidth: "665px",
                  marginBottom: "32px",
                }}
              >
                If You're Ready To Move Fast And Build Something That
                Matters, Apply Now.
              </p>

              {/* CTA Button */}
              <button
                onClick={scrollToForm}
                className="whitespace-nowrap transition-transform hover:scale-105"
                style={{
                  width: "389px",
                  height: "59px",
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 600,
                  fontSize: "18px",
                  letterSpacing: "-0.05em",
                  color: "#000000",
                  background: "#B0F18D",
                  borderRadius: "11px",
                  border: "none",
                  cursor: "pointer",
                  marginBottom: "24px",
                }}
              >
                Apply For The Paid Marketing Internship
              </button>

              {/* Checkmark note */}
              <div className="flex items-center gap-3">
                <Check
                  className="w-5 h-5 flex-shrink-0"
                  style={{ color: "#B0F18D" }}
                />
                <p
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 600,
                    fontSize: "14px",
                    lineHeight: "39px",
                    letterSpacing: "-0.05em",
                    textTransform: "capitalize" as const,
                    color: "#FFFFFFB2",
                  }}
                >
                  Spots Are Limited Per Semester. Not Urgency Hype. Just Clarity.
                </p>
              </div>
            </div>

            {/* Right image */}
            <div className="flex-1 flex justify-end">
              <img
                src={ctaCollabPhoto}
                alt="Interns collaborating on a video shoot"
                style={{
                  width: "566px",
                  height: "378px",
                  borderRadius: "40px",
                  objectFit: "cover",
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Footer contact bar */}
      <section
        style={{
          background: "#B0F18D",
          padding: "20px 0",
        }}
      >
        <div className="max-w-[1514px] w-full mx-auto px-4 md:px-8 lg:px-[76px]">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-black" />
              <span
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 600,
                  fontSize: "16px",
                  letterSpacing: "-0.05em",
                  color: "#000000",
                }}
              >
                support@perchmobile.com
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-black" />
              <span
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 600,
                  fontSize: "16px",
                  letterSpacing: "-0.05em",
                  color: "#000000",
                }}
              >
                Text us at 833-736-5690
              </span>
            </div>
            <div className="flex items-center gap-3">
              <MapPin className="w-5 h-5 text-black" />
              <span
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 600,
                  fontSize: "16px",
                  letterSpacing: "-0.05em",
                  color: "#000000",
                }}
              >
                195 Montague St Brooklyn NY 11201
              </span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PerchInternship;
